import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { useRegisterMutation } from '../slices/usersApiSlice';
// NOTE: useGetManagersQuery removed — managers data is not used in the
// registration form and the /managers endpoint now requires auth (protect).
// Calling it here caused "Not authorized, no token" for unauthenticated visitors.

import { toast } from 'react-toastify';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice.js';
import { logout } from '../slices/authSlice';
import { CATEGORY_OF_STAFF } from '../utils/enums';

// ─── If your hospitalLocationRoutes also use `protect`, you have two options:
//
//   Option A (recommended): Remove `protect` from the GET / (list all) route
//   in hospitalLocationRoutes.js — location data is not sensitive and is
//   needed at registration time before any JWT exists.
//
//   Option B: Skip the location dropdown on the register page entirely and
//   let the user/admin fill it in via the profile screen after approval.
//
// ─────────────────────────────────────────────────────────────────────────────

const dropdownStyle = { backgroundColor: '#F5F5F5' };
const textStyle     = { backgroundColor: '#f5f5dc' };

const RegisterScreen = () => {
  const [name,            setName]            = useState('-');
  const [email,           setEmail]           = useState('');
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstname,       setFirstname]       = useState('');
  const [lastname,        setLastname]        = useState('');
  const [employeenumber,  setEmployeenumber]  = useState('');
  const [jobtitle,        setJobtitle]        = useState('');
  const [salutation,      setSalutation]      = useState('--');
  const [comment,         setComment]         = useState('--');
  const [effectiveDate,   setEffectiveDate]   = useState(new Date());
  const [reviewDate,      setReviewDate]      = useState(new Date());
  const [openingBalance,  setOpeningBalance]  = useState(0);
  const [bringForward,    setBringForward]    = useState(0);

  // Location fields
  const [speciality,           setSpeciality]           = useState(''); // wardDepartmentArea
  const [specialityID,         setSpecialityID]         = useState(''); // hospitalLocation _id
  const [directoratedepartment,setDirectoratedepartment]= useState(''); // directorate name
  const [directorateID,        setDirectorateID]        = useState(''); // hospitalLocation _id

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [register, { isLoading }] = useRegisterMutation();

  // Hospital locations — public endpoint (no auth required).
  // If you see a 401 here, remove `protect` from the GET route in
  // hospitalLocationRoutes.js for the list-all endpoint.
  const {
    data: hospitalLocations,
    isLoading: isLoadingHL,
    error: errorHL,
  } = useGetHospitalLocationsQuery();

  const { userInfo } = useSelector((state) => state.auth);
  const { search }   = useLocation();
  const redirect     = new URLSearchParams(search).get('redirect') || '/';

  // Redirect already-logged-in users away from register page
  useEffect(() => {
    if (userInfo) navigate(redirect);
  }, [navigate, redirect, userInfo]);

  // ── Group hospital locations by directorate ──────────────────────────────
  const locationsByDirectorate = (hospitalLocations ?? []).reduce((acc, loc) => {
    if (!acc[loc.directorate]) acc[loc.directorate] = [];
    acc[loc.directorate].push(loc);
    return acc;
  }, {});
  const directorates = Object.keys(locationsByDirectorate).sort();

  // ── Submit ────────────────────────────────────────────────────────────────
  const submitHandler = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await register({
        name: `${firstname} ${lastname}`,
        email,
        password,
        employeenumber,
        firstname,
        lastname,
        jobtitle,
        speciality,       // wardDepartmentArea
        specialityID,     // hospitalLocation _id
        directoratedepartment,
        directorateID,
        effectiveDate,
        reviewDate,
        comment,
        salutation,
        openingBalance,
        bringForward,
      }).unwrap();

      dispatch(logout());
      toast.success('Registration successful. Please log in once your account is approved by Admin.');
      navigate('/login?message=registration-pending');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isLoadingHL) return <Loader />;

  if (errorHL) {
    // Friendly message if the hospital-locations endpoint is also protected
    const isAuthError =
      errorHL?.status === 401 ||
      errorHL?.data?.message?.toLowerCase().includes('not authorized');

    return (
      <Container fluid className="mt-4">
        <Message variant="danger">
          {isAuthError
            ? 'Could not load location data. '
            : errorHL?.data?.message || 'Error loading hospital locations'}
        </Message>
        <Row className="py-3">
          <Col>
            Already have an account?{' '}
            <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>Login</Link>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1>Register</h1>

      <Form onSubmit={submitHandler}>
        {/* Registering as — staff only for now */}
        <Form.Group controlId="userType">
          <Form.Label>Registering as</Form.Label>
          <Form.Control as="select" defaultValue="staff" disabled style={textStyle}>
            <option value="staff">Staff Member</option>
          </Form.Control>
        </Form.Group>

        <Row className="mt-3">
          {/* ── Column 1: Personal details + password ── */}
          <Col md={5}>
            <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
              <Row>
                <Col sm={2}>
                  <Form.Group controlId="salutation">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      as="select"
                      value={salutation}
                      onChange={(e) => setSalutation(e.target.value)}
                      style={textStyle}
                    >
                      <option value="">Select</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Professor">Professor</option>
                      <option value="Mx">Mx</option>
                    </Form.Control>
                  </Form.Group>
                </Col>

                <Col sm={5}>
                  <Form.Group controlId="firstname">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="First Name"
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      style={textStyle}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col sm={5}>
                  <Form.Group controlId="lastname">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Last Name"
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value)}
                      style={textStyle}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="email" className="mt-2">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={textStyle}
                  required
                />
              </Form.Group>

              <Row className="mt-2">
                <Col sm={6}>
                  <Form.Group controlId="employeenumber">
                    <Form.Label>Employee Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Employee Number"
                      value={employeenumber}
                      onChange={(e) => setEmployeenumber(e.target.value)}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="jobtitle">
                    <Form.Label>Job Title / Category</Form.Label>
                    <Form.Control
                      as="select"
                      value={jobtitle}
                      onChange={(e) => setJobtitle(e.target.value)}
                      style={textStyle}
                    >
                      <option value="">Select Job Title</option>
                      {CATEGORY_OF_STAFF.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-2">
                <Col sm={6}>
                  <Form.Group controlId="effectiveDate">
                    <Form.Label>Effective Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="reviewDate">
                    <Form.Label>Review Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={reviewDate}
                      onChange={(e) => setReviewDate(e.target.value)}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="mt-3">
              <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
                <Form.Group controlId="password">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Set password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={textStyle}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="confirmPassword" className="mt-2">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={textStyle}
                    required
                  />
                </Form.Group>
              </div>
            </div>
          </Col>

          {/* ── Column 2: Hospital location ── */}
          <Col md={4}>
            <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
              <Form.Group controlId="setDirectorate">
                <Form.Label>Directorate</Form.Label>
                <Form.Control
                  as="select"
                  value={directoratedepartment}
                  onChange={(e) => {
                    setDirectoratedepartment(e.target.value);
                    // Clear ward when directorate changes
                    setSpeciality('');
                    setSpecialityID('');
                    setDirectorateID('');
                  }}
                  style={textStyle}
                >
                  <option value="">Select Directorate</option>
                  {directorates.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="wardDepartmentArea" className="mt-3">
                <Form.Label>Ward / Department normally assigned to</Form.Label>
                <Form.Control
                  as="select"
                  value={speciality}
                  onChange={(e) => {
                    const idx         = e.target.selectedIndex;
                    const opts        = e.target.options;
                    const selectedVal = opts[idx].value;
                    const selectedId  = opts[idx].getAttribute('data-id');
                    const selectedDir = opts[idx].getAttribute('data-directorate');
                    setSpeciality(selectedVal);
                    setSpecialityID(selectedId);
                    setDirectorateID(selectedId);
                    if (selectedDir) setDirectoratedepartment(selectedDir);
                  }}
                  disabled={!directoratedepartment}
                  style={textStyle}
                >
                  <option value="">
                    {directoratedepartment ? 'Select Ward / Department' : 'Select a directorate first'}
                  </option>
                  {directoratedepartment &&
                    locationsByDirectorate[directoratedepartment]?.map((loc) => (
                      <option
                        key={loc._id}
                        value={loc.wardDepartmentArea}
                        data-id={loc._id}
                        data-directorate={loc.directorate}
                      >
                        {loc.wardDepartmentArea}
                        {loc.department && ` - ${loc.department}`}
                        {loc.costCentreCode && ` (${loc.costCentreCode})`}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>

              {specialityID && (
                <div className="mt-2 p-2 bg-light rounded">
                  <small>
                    <strong>Selected:</strong><br />
                    Directorate: {directoratedepartment}<br />
                    Ward/Area: {speciality}
                  </small>
                </div>
              )}
            </div>
          </Col>

          {/* ── Column 3: Comments + balances ── */}
          <Col md={3}>
            <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '10px', backgroundColor: '#F0F8FF' }}>
              <Form.Group controlId="comment">
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Comments"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  style={textStyle}
                />
              </Form.Group>

              <Row className="mt-3">
                <Col sm={6}>
                  <Form.Group controlId="openingBalance">
                    <Form.Label>AL Balance (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.10"
                      placeholder="0.00"
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(Number(e.target.value))}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group controlId="bringForward">
                    <Form.Label>Bring Forward (Hours)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.10"
                      placeholder="0.00"
                      value={bringForward}
                      onChange={(e) => setBringForward(Number(e.target.value))}
                      style={textStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        <Button disabled={isLoading} type="submit" variant="primary" className="mt-3">
          Register
        </Button>

        {isLoading && <Loader />}
      </Form>

      <Row className="py-3">
        <Col>
          Already have an account?{' '}
          <Link to={redirect ? `/login?redirect=${redirect}` : '/login'}>Login</Link>
        </Col>
      </Row>
    </Container>
  );
};

export default RegisterScreen;